import { format, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { PROCESSING_LABELS } from '_constants/processing';
import { theme } from '_constants/theme';
import { formatPrice } from '_helpers/common';

import Button from 'components/shared/Button';
import Indicator from 'components/shared/Indicator';
import Paginate from 'components/shared/Paginate';
import Table from 'components/shared/Table';
import Text from 'components/shared/layouts/Text';

const StyledContainer = styled.div`
  .paginate-container {
    padding-top: ${theme.spaces.large}px;
  }
`;

const StyledLinkContainer = styled.div`
  a {
    font-family: 'RethinkSans';
    font-size: ${theme.fontSizes.regular}px;
    font-weight: 400;
    line-height: 16px;
    color: ${theme.colors.deepBlueGreen};

    &:hover {
      color: ${theme.colors.cyanBlue};
    }
  }
`;

const getColumns = ({ onUnsubscribe }) => {
  const columns = [
    {
      dataIndex: 'crmOrderId',
      title: 'ORDER ID',
      width: '12%',
      render: (row) => (
        <StyledLinkContainer>
          <Link to={`../order/${row?.id}`}>{row?.orderId}</Link>
        </StyledLinkContainer>
      ),
    },
    {
      dataIndex: 'orderDate',
      title: 'DATE PLACED',
      width: '12%',
      render: (row) => <Text>{row?.orderDate}</Text>,
    },
    {
      dataIndex: 'statusIndicator',
      title: null,
      width: '2%',
      minWidth: '16px',
      render: (row) => <Indicator status={row?.status} />,
    },
    {
      dataIndex: 'statusValue',
      title: 'STATUS',
      width: '12%',
      render: (row) => <Text>{row?.status}</Text>,
    },
    {
      dataIndex: 'productName',
      title: 'MEDICATION',
      width: '15%',
      render: (row) => <Text>{row?.productName}</Text>,
    },
    {
      dataIndex: 'price',
      title: 'PRICE',
      width: '8%',
      right: true,
      render: (row) => <Text>{formatPrice(row?.price)}</Text>,
    },
    {
      dataIndex: 'span',
      title: null,
      width: '2%',
    },
    {
      dataIndex: 'nextPayDate',
      title: 'NEXT BILLING DATE',
      width: '15%',
      render: (row) => (
        <Text>
          {row?.nextPayDate && !row?.nextPayDate?.includes('null')
            ? format(parseISO(row.nextPayDate), 'MMMM dd, yyyy')
            : '-'}
        </Text>
      ),
    },
    {
      dataIndex: 'processingOption',
      title: 'PROCESSING',
      width: '8%',
      render: (row) => <Text>{PROCESSING_LABELS[row?.processingOption]}</Text>,
    },
    {
      dataIndex: 'cancelAction',
      title: null,
      width: '14%',
      render: (row) => {
        if (row?.id) {
          return (
            <Button
              small
              outlined
              disabled={row?.status === 'CANCELLED'}
              color={theme.colors.red.primary}
              onClick={onUnsubscribe({
                orderId: row?.id,
                productId: row?.productId,
                productName: row?.productName,
              })}
            >
              Unsubscribe
            </Button>
          );
        }
      },
    },
  ];

  return columns;
};

const SubscriptionsTable = ({
  skeletonRowsNum,
  loading,
  items,
  pageCount,
  currentPage,
  onPageChange,
  onUnsubscribe,
}) => {
  return (
    <StyledContainer>
      <Table columns={getColumns({ onUnsubscribe })} rows={items} loading={loading} skeletonRowsNum={skeletonRowsNum} />
      {!loading && !items?.length && <Text centered>No subscriptions found</Text>}
      <div className="paginate-container">
        <Paginate pageRangeDisplayed={3} pageCount={pageCount} currentPage={currentPage} onPageChange={onPageChange} />
      </div>
    </StyledContainer>
  );
};

SubscriptionsTable.propTypes = {
  skeletonRowsNum: PropTypes.bool,
  loading: PropTypes.bool,
  items: PropTypes.array,
  pageCount: PropTypes.number,
  currentPage: PropTypes.number,
  onPageChange: PropTypes.func,
  onUnsubscribe: PropTypes.func,
};

export default SubscriptionsTable;
